import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from '../../molecules/Card';
import LoadingSpinner from '../../atoms/LoadingSpinner';
import { fetchSystemHealth, fetchServiceHistory } from '../../../services/systemHealthService';
import ServiceDetailPanel from './components/ServiceDetailPanel';
import ServiceHealthPreview from './components/ServiceHealthPreview';
import VenueReviewPanel from './components/VenueReviewPanel';
import { isAdminBillingUiEnabled } from '../../../constants/billingFeatures';

import './SystemHealthPage.scss';

const AdminBillingPage = lazy(() => import('../AdminBillingPage'));

const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const HISTORY_RANGES = [
    { label: '1h', value: 'PT1H' },
    { label: '6h', value: 'PT6H' },
    { label: '24h', value: 'P1D' },
    { label: '7d', value: 'P7D' },
];
const DEFAULT_HISTORY_RANGE = HISTORY_RANGES[1].value;
const MAX_HISTORY_SAMPLES = 720;
const SYSTEM_SECTIONS = Object.freeze({
    HEALTH: 'health',
    VENUE_APPROVALS: 'venue-approvals',
    BILLING: 'billing',
});
const SYSTEM_SECTION_QUERY_PARAM = 'section';

const normalizeSystemSectionId = (value) => {
    if (typeof value !== 'string') {
        return null;
    }
    const normalized = value.trim().toLowerCase();
    return Object.values(SYSTEM_SECTIONS).includes(normalized) ? normalized : null;
};

const resolveSystemSectionFromSearch = (search, sectionTabs) => {
    const requestedSection = normalizeSystemSectionId(
        new URLSearchParams(search).get(SYSTEM_SECTION_QUERY_PARAM),
    );
    if (requestedSection && sectionTabs.some((tab) => tab.id === requestedSection)) {
        return requestedSection;
    }
    return SYSTEM_SECTIONS.HEALTH;
};

const buildSystemSectionSearch = (currentSearch, sectionId) => {
    const params = new URLSearchParams(currentSearch);
    if (sectionId === SYSTEM_SECTIONS.HEALTH) {
        params.delete(SYSTEM_SECTION_QUERY_PARAM);
    } else {
        params.set(SYSTEM_SECTION_QUERY_PARAM, sectionId);
    }
    const nextSearch = params.toString();
    return nextSearch ? `?${nextSearch}` : '';
};

const SystemHealthPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const adminBillingEnabled = useMemo(() => isAdminBillingUiEnabled(), []);
    const [snapshot, setSnapshot] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedServiceId, setSelectedServiceId] = useState(null);
    const [historyRange, setHistoryRange] = useState(DEFAULT_HISTORY_RANGE);
    const [history, setHistory] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const [historyReloadToken, setHistoryReloadToken] = useState(0);

    const loadSnapshot = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchSystemHealth();
            setSnapshot(data);
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || 'Unable to load system health data';
            setError(message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleSelectService = useCallback((serviceId) => {
        setSelectedServiceId(serviceId);
    }, []);

    const handleRangeChange = useCallback((value) => {
        setHistoryRange(value);
    }, []);

    const handleRetryHistory = useCallback(() => {
        setHistoryReloadToken((token) => token + 1);
    }, []);

    const sectionTabs = useMemo(() => ([
        { id: SYSTEM_SECTIONS.HEALTH, label: 'Services' },
        { id: SYSTEM_SECTIONS.VENUE_APPROVALS, label: 'Venue Approvals' },
        ...(adminBillingEnabled ? [{ id: SYSTEM_SECTIONS.BILLING, label: 'Billing' }] : []),
    ]), [adminBillingEnabled]);
    const [activeSection, setActiveSection] = useState(() => (
        resolveSystemSectionFromSearch(location.search, sectionTabs)
    ));

    useEffect(() => {
        setActiveSection((currentSection) => {
            const nextSection = resolveSystemSectionFromSearch(location.search, sectionTabs);
            return currentSection === nextSection ? currentSection : nextSection;
        });
    }, [location.search, sectionTabs]);

    const handleSectionChange = useCallback((sectionId) => {
        if (!sectionTabs.some((tab) => tab.id === sectionId)) {
            return;
        }
        setActiveSection(sectionId);
        const nextSearch = buildSystemSectionSearch(location.search, sectionId);
        if (nextSearch !== location.search) {
            navigate({
                pathname: location.pathname,
                search: nextSearch,
            }, { replace: true });
        }
    }, [location.pathname, location.search, navigate, sectionTabs]);

    useEffect(() => {
        if (activeSection !== SYSTEM_SECTIONS.HEALTH) {
            return undefined;
        }

        let cancelled = false;
        let timerId = null;

        const initialise = async () => {
            await loadSnapshot();
            if (!cancelled) {
                timerId = setInterval(loadSnapshot, DEFAULT_POLL_INTERVAL_MS);
            }
        };

        initialise();

        return () => {
            cancelled = true;
            if (timerId) {
                clearInterval(timerId);
            }
        };
    }, [activeSection, loadSnapshot]);

    useEffect(() => {
        if (activeSection !== SYSTEM_SECTIONS.HEALTH) {
            setSelectedServiceId(null);
            return;
        }
        if (!snapshot?.services || snapshot.services.length === 0) {
            setSelectedServiceId(null);
            return;
        }
        setSelectedServiceId((current) => {
            if (current && snapshot.services.some((service) => service.id === current)) {
                return current;
            }
            return snapshot.services[0].id;
        });
    }, [activeSection, snapshot]);

    useEffect(() => {
        if (activeSection !== SYSTEM_SECTIONS.HEALTH) {
            setHistory(null);
            setHistoryError(null);
            setHistoryLoading(false);
            return undefined;
        }
        if (!selectedServiceId) {
            setHistory(null);
            setHistoryError(null);
            setHistoryLoading(false);
            return undefined;
        }
        let cancelled = false;

        const loadHistory = async () => {
            setHistoryLoading(true);
            setHistoryError(null);
            try {
                const data = await fetchServiceHistory(selectedServiceId, {
                    range: historyRange,
                    maxSamples: MAX_HISTORY_SAMPLES,
                });
                if (!cancelled) {
                    setHistory(data);
                }
            } catch (err) {
                if (!cancelled) {
                    const message = err?.response?.data?.message || err?.message || 'Unable to load history';
                    setHistoryError(message);
                    setHistory(null);
                }
            } finally {
                if (!cancelled) {
                    setHistoryLoading(false);
                }
            }
        };

        loadHistory();

        return () => {
            cancelled = true;
        };
    }, [activeSection, selectedServiceId, historyRange, snapshot?.generatedAt, historyReloadToken]);

    const services = useMemo(() => snapshot?.services || [], [snapshot]);

    const generatedAt = useMemo(() => {
        if (!snapshot?.generatedAt) {
            return null;
        }
        return new Date(snapshot.generatedAt);
    }, [snapshot]);

    const selectedService = useMemo(() => {
        if (!selectedServiceId) {
            return null;
        }
        return services.find((service) => service.id === selectedServiceId) || null;
    }, [services, selectedServiceId]);

    return (
        <div className="system-health-page">
            <header className="system-health-page__header">
                <div className="system-health-page__intro">
                    <h1 className="system-health-page__title">System</h1>
                </div>
                {activeSection === SYSTEM_SECTIONS.HEALTH ? (
                    <button
                        type="button"
                        className="btn-primary system-health-page__refresh"
                        onClick={loadSnapshot}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                ) : null}
            </header>

            <nav className="system-health-page__section-tabs" aria-label="System administration sections">
                {sectionTabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        className={`system-health-page__section-tab${activeSection === tab.id ? ' system-health-page__section-tab--active' : ''}`}
                        onClick={() => handleSectionChange(tab.id)}
                        aria-pressed={activeSection === tab.id}
                    >
                        {tab.label}
                    </button>
                ))}
            </nav>

            {activeSection === SYSTEM_SECTIONS.HEALTH && error && (
                <div className="system-health-page__alert system-health-page__alert--error">
                    {error}
                </div>
            )}

            {activeSection === SYSTEM_SECTIONS.HEALTH ? (
                <div className="system-health-page__layout">
                    <Card
                        className="system-health-page__summary"
                        allowOverflow
                        title={<SummaryHeader generatedAt={generatedAt} isLoading={isLoading} hasSnapshot={Boolean(snapshot)} />}
                        body={(
                            <SummaryBody
                                isInitialLoading={isLoading && !snapshot}
                                services={services}
                                selectedServiceId={selectedServiceId}
                                onSelectService={handleSelectService}
                            />
                        )}
                        footer={
                            <p className="system-health-page__footnote">
                                Access restricted to administrators. Metrics are cached server-side to prevent excessive load.
                                Monitor backend services, uptime, and request performance. Data refreshes automatically.
                            </p>
                        }
                    />

                    <ServiceDetailPanel
                        service={selectedService}
                        history={history}
                        range={historyRange}
                        rangeOptions={HISTORY_RANGES}
                        onRangeChange={handleRangeChange}
                        isLoading={historyLoading}
                        error={historyError}
                        onRetry={handleRetryHistory}
                    />
                </div>
            ) : null}

            {activeSection === SYSTEM_SECTIONS.VENUE_APPROVALS ? (
                <VenueReviewPanel />
            ) : null}

            {activeSection === SYSTEM_SECTIONS.BILLING && adminBillingEnabled ? (
                <div className="system-health-page__billing">
                    <Suspense
                        fallback={(
                            <div className="system-health-page__loader" role="status" aria-live="polite">
                                <LoadingSpinner size={24} />
                                Loading billing administration...
                            </div>
                        )}
                    >
                        <AdminBillingPage />
                    </Suspense>
                </div>
            ) : null}
        </div>
    );
};

const SummaryHeader = ({ generatedAt = null, isLoading = false, hasSnapshot = false }) => (
    <div className="system-health-page__summary-header">
        <div>
            <h2>Service Overview</h2>
            <p className="system-health-page__summary-meta">
                {generatedAt ? `Last updated ${generatedAt.toLocaleString()}` : 'Awaiting first snapshot'}
            </p>
        </div>
        {isLoading && hasSnapshot && <span className="system-health-page__summary-loading">Refreshing...</span>}
    </div>
);

SummaryHeader.propTypes = {
    generatedAt: PropTypes.instanceOf(Date),
    isLoading: PropTypes.bool,
    hasSnapshot: PropTypes.bool,
};

const SummaryBody = ({
    isInitialLoading = false,
    services,
    selectedServiceId = null,
    onSelectService = () => {},
}) => (
    <div className="system-health-page__body">
        {isInitialLoading ? (
            <div className="system-health-page__loader" role="status" aria-live="polite">
                Loading latest metrics...
            </div>
        ) : (
            <ServiceGrid services={services} selectedServiceId={selectedServiceId} onSelectService={onSelectService} />
        )}
    </div>
);

SummaryBody.propTypes = {
    isInitialLoading: PropTypes.bool,
    services: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
    })).isRequired,
    selectedServiceId: PropTypes.string,
    onSelectService: PropTypes.func,
};

const ServiceGrid = ({ services, selectedServiceId = null, onSelectService = () => {} }) => {
    if (!services.length) {
        return (
            <div className="system-health-page__empty">
                <p>No service telemetry available yet.</p>
            </div>
        );
    }
    return (
        <div className="system-health-page__list scroll-container">
            {services.map((service) => (
                <ServiceHealthPreview
                    key={service.id}
                    service={service}
                    onSelect={onSelectService}
                    isExpanded={service.id === selectedServiceId}
                />
            ))}
        </div>
    );
};

ServiceGrid.propTypes = {
    services: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
    })).isRequired,
    selectedServiceId: PropTypes.string,
    onSelectService: PropTypes.func,
};

export default SystemHealthPage;
